import { useState, useEffect } from 'react';
import { FilterModalProps } from '../types/FilterModalProps';
import { Button } from './Button';
import { Radio } from './forms/Radio';
import { RadioGroup } from './forms/RadioGroup';
import { Close } from './icons/Close';

export const FilterModal = ({
  isOpen,
  onClose,
  selectedGender,
  selectedEducation,
  applyChanges,
  resetChanges
}: FilterModalProps) => {
  const [gender, setGender] = useState(selectedGender);
  const [education, setEducation] = useState(selectedEducation);

  // Sync local state with prop changes
  useEffect(() => {
    setGender(selectedGender);
  }, [selectedGender]);

  useEffect(() => {
    setEducation(selectedEducation);
  }, [selectedEducation]);

  

  const handleApply = () => {
    applyChanges(gender, education);
  };

  const handleReset = () => {
    setGender('All');
    setEducation('All');
    resetChanges();
  };

  return isOpen ? (

    <div className='absolute top-0 bottom-0 left-0 right-0 max-h-lvh mt-12 pb-12 md:pb-0 mb-0 md:mb-0 rounded-3xl z-20 bg-white px-5 pt-8 box-content flex flex-col md:justify-between'>
      <div className='bg-white mr-neg-5 md:mr-0 pr-5 md:pr-0 max-md:pb-20 max-md:overflow-y-scroll'>
        <Button
          circle
          colour='golden'
          className='absolute mt-neg-15 mt- right-4'
          onClick={onClose}>
          <Close variant='dark' />
        </Button>
        <h2 className='text-2xl text-bluestone-light mb-8'>Filter</h2>
        <RadioGroup groupHeading='Gender'>
          <Radio
            groupName='gender'
            option='All'
            value='All'
            checked={gender === 'All'}
            onChange={() => setGender('All')}
          />
          <Radio
            groupName='gender'
            option='Boys'
            value='Boys'
            checked={gender === 'Boys'}
            onChange={() => setGender('Boys')}
          />
          <Radio
            groupName='gender'
            option='Girls'
            value='Girls'
            checked={gender === 'Girls'}
            onChange={() => setGender('Girls')}
          />
          <Radio
            groupName='gender'
            option='Co-ed (mixed)'
            value='Co-ed (mixed)'
            checked={gender === 'Co-ed (mixed)'}
            onChange={() => setGender('Co-ed (mixed)')}
          />
        </RadioGroup>
        <RadioGroup groupHeading='Education Level'>
          <Radio
            groupName='educationLevel'
            option='All'
            value='All'
            checked={education === 'All'}
            onChange={() => setEducation('All')}
          />
          <Radio
            groupName='educationLevel'
            option='Primary ( 2-6 )'
            value='Primary'
            checked={education === 'Primary'}
            onChange={() => setEducation('Primary')}
            icon={
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='24'
                height='24'
                fill='none'>
                <rect width='24' height='24' fill='#00ADEA' rx='12' />
                <path
                  fill='#0B223E'
                  d='M15.078 9.606h-1.71v-1.71a.684.684 0 0 0-.684-.684h-1.368a.684.684 0 0 0-.684.684v1.71h-1.71a.684.684 0 0 0-.684.684v1.368c0 .378.306.684.684.684h1.71v3.762c0 .378.306.684.684.684h1.368a.684.684 0 0 0 .684-.684v-3.762h1.71a.684.684 0 0 0 .684-.684V10.29a.684.684 0 0 0-.684-.684Zm0 2.052h-2.052a.342.342 0 0 0-.342.342v4.104h-1.368V12a.342.342 0 0 0-.342-.342H8.922V10.29h2.052a.342.342 0 0 0 .342-.342V7.896h1.368v2.052c0 .189.153.342.342.342h2.052v1.368Z'
                />
              </svg>
            }
          />
          <Radio
            groupName='educationLevel'
            option='Secondary ( 7-12 )'
            value='Secondary'
            checked={education === 'Secondary'}
            onChange={() => setEducation('Secondary')}
            icon={
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='24'
                height='24'
                fill='none'>
                <rect width='24' height='24' fill='#0B223E' rx='12' />
                <path
                  fill='#fff'
                  d='M15.078 9.606h-1.71v-1.71a.684.684 0 0 0-.684-.684h-1.368a.684.684 0 0 0-.684.684v1.71h-1.71a.684.684 0 0 0-.684.684v1.368c0 .378.306.684.684.684h1.71v3.762c0 .378.306.684.684.684h1.368a.684.684 0 0 0 .684-.684v-3.762h1.71a.684.684 0 0 0 .684-.684V10.29a.684.684 0 0 0-.684-.684Zm0 2.052h-2.052a.342.342 0 0 0-.342.342v4.104h-1.368V12a.342.342 0 0 0-.342-.342H8.922V10.29h2.052a.342.342 0 0 0 .342-.342V7.896h1.368v2.052c0 .189.153.342.342.342h2.052v1.368Z'
                />
              </svg>
            }
          />
          <Radio
            groupName='educationLevel'
            option='K-12'
            value='K-12'
            checked={education === 'K-12'}
            onChange={() => setEducation('K-12')}
            icon={
              <svg
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'>
                <path
                  d='M0 12C0 5.37258 5.37258 0 12 0C18.6274 0 24 5.37258 24 12C24 18.6274 18.6274 24 12 24C5.37258 24 0 18.6274 0 12Z'
                  fill='#FFDF00'
                />
                <path
                  d='M15.078 9.60601H13.368V7.89601C13.368 7.51824 13.0618 7.21201 12.684 7.21201H11.316C10.9383 7.21201 10.632 7.51824 10.632 7.89601V9.60601H8.92204C8.54426 9.60599 8.23804 9.91223 8.23804 10.29V11.658C8.23804 12.0358 8.54426 12.342 8.92204 12.342H10.632V16.104C10.632 16.4818 10.9383 16.788 11.316 16.788H12.684C13.0618 16.788 13.368 16.4818 13.368 16.104V12.342H15.078C15.4558 12.342 15.762 12.0358 15.762 11.658V10.29C15.762 9.91223 15.4558 9.60599 15.078 9.60601ZM15.078 11.658H13.026C12.8372 11.658 12.684 11.8111 12.684 12V16.104H11.316V12C11.316 11.8111 11.1629 11.658 10.974 11.658H8.92204V10.29H10.974C11.1629 10.29 11.316 10.1369 11.316 9.94801V7.89601H12.684V9.94801C12.684 10.1369 12.8372 10.29 13.026 10.29H15.078V11.658Z'
                  fill='#0B223E'
                />
              </svg>
            }
          />
          <Radio
            groupName='educationLevel'
            option='Specialist School'
            value='Specialist School'
            checked={education === 'Specialist School'}
            onChange={() => setEducation('Specialist School')}
            icon={
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='24'
                height='24'
                fill='none'>
                <path
                  fill='#1DA896'
                  d='M0 12C0 5.373 5.373 0 12 0s12 5.373 12 12-5.373 12-12 12S0 18.627 0 12Z'
                />
                <path
                  fill='#fff'
                  d='M15.078 9.606h-1.71v-1.71a.684.684 0 0 0-.684-.684h-1.368a.684.684 0 0 0-.684.684v1.71h-1.71a.684.684 0 0 0-.684.684v1.368c0 .378.306.684.684.684h1.71v3.762c0 .378.306.684.684.684h1.368a.684.684 0 0 0 .684-.684v-3.762h1.71a.684.684 0 0 0 .684-.684V10.29a.684.684 0 0 0-.684-.684Zm0 2.052h-2.052a.342.342 0 0 0-.342.342v4.104h-1.368V12a.342.342 0 0 0-.342-.342H8.922V10.29h2.052a.342.342 0 0 0 .342-.342V7.896h1.368v2.052c0 .189.153.342.342.342h2.052v1.368Z'
                />
              </svg>
            }
          />
        </RadioGroup>
      </div>
      <div className='flex gap-4 px-5 py-6 max-md:absolute max-md:self-center max-md:bottom-0 max-md:w-full'>
        <Button
          colour='golden'
          label='Apply'
          className='w-1/2'
          onClick={handleApply}
        />
        <Button
          colour='white'
          label='Reset'
          className='w-1/2'
          onClick={handleReset}
        />
      </div>
    </div>
  ) : null;
};
